import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { formatDateDb } from "../utils/tools";
import { customDateFormatType } from "../utils/utils";

const NUMBER_NFT_PER_PAGE = 25;
const queryKey = "employeeKPIList";

async function fetchEmployeeKPIList(
  currentPage,
  code,
  monthDate,
  saleBlock,
  saleArea,
  status,
) {
  try {
    const options = { limit: NUMBER_NFT_PER_PAGE, page: currentPage };

    if (code) options.code = code;
    if (saleBlock) options.saleBlockId = saleBlock;
    if (saleArea) options.saleAreaId = saleArea;
    if (status) options.status = status;
    if (monthDate) {
      options.month = Number(monthDate.getMonth() + 1);
      options.year = monthDate.getFullYear();
    }

    const queryParams = new URLSearchParams(options).toString();
    const data = await clientAPI(
      "get",
      `/document/api/v2/msh/evaluations/sale-kpis/monthly?${queryParams}`,
    );

    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries,
    );

    const newData = data?.items.map((item, index) => {
      return {
        id: item?._id || "",
        employeeId: item?.employee?.code || "",
        code: item?.employeeObject?.code || item?.employee?.code || "-",
        name: item?.employeeObject?.fullName || item?.employee?.fullName || "-",
        branch: item?.employee?.branch?.name || "-",
        saleBlock: item?.employee?.block?.name || "-",
        saleArea: item?.employee?.area?.name || "-",
        leadCount: item?.metrics?.leadCount || 0,
        consultCount: item?.metrics?.consultCount || 0,
        transactionCount: item?.metrics?.transactionCount || 0,
        classification: item?.classification?.code || "-",
        note: item?.note || "-",
        calculatedAt: item?.calculatedAt
          ? formatDateDb(item?.calculatedAt)
          : "-",
      };
    });

    const newSummary = data?.summary || {
      totalSales: 0,
      classifications: {
        XUAT_SAC: 0,
        DAT: 0,
        CANH_BAO: 0,
        CAN_HO_TRO: 0,
      },
    };

    return {
      data: newData,
      totalPages,
      entries,
      startEntry,
      endEntry,
      summary: newSummary,
    };
  } catch (error) {
    console.error("Error fetching checkIn data:", error);
    return {
      data: [],
      totalPages: 0,
      summary: {
        totalSales: 0,
        classifications: {
          XUAT_SAC: 0,
          DAT: 0,
          CANH_BAO: 0,
          CAN_HO_TRO: 0,
        },
      },
    };
  }
}

export function useEmployeeKPIList(
  search,
  monthDate,
  saleBlock,
  saleArea,
  status,
) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [
      queryKey,
      currentPage,
      ,
      search,
      monthDate,
      saleBlock,
      saleArea,
      status,
    ],
    queryFn: () =>
      fetchEmployeeKPIList(
        currentPage,
        search,
        monthDate,
        saleBlock,
        saleArea,
        status,
      ),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching checkIn data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < data?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    employeeKPIList: data?.data,
    summary: data?.summary,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}
